import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  disconnectSmartcar,
  getSmartcarConnectUrl,
  getSmartcarData,
  refreshSmartcarData,
  SmartcarData,
} from '../../api/smartcar.api';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format a number with locale comma separators. */
const fmtNumber = (n: number): string =>
  n.toLocaleString('fr-FR');

/** Return how many minutes ago a UTC ISO string was. */
const minutesAgo = (iso: string): number => {
  const diff = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(diff / 60_000));
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Animated skeleton placeholder */
const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

/** Progress bar for fuel / battery percentages */
const PercentBar: React.FC<{ pct: number; color: string }> = ({ pct, color }) => {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="mt-1.5 h-2 w-full bg-gray-200 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full ${color} transition-all duration-300`}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
};

/** Charge state badge */
const ChargeStateBadge: React.FC<{ state: SmartcarData['charge_state'] }> = ({ state }) => {
  const { t } = useTranslation();
  if (!state) return <span className="text-gray-400 text-sm">—</span>;
  const map: Record<NonNullable<SmartcarData['charge_state']>, { variant: 'success' | 'professional' | 'pending'; label: string }> = {
    FULLY_CHARGED: { variant: 'success', label: t('smartcar.chargeStateFull') },
    CHARGING: { variant: 'professional', label: t('smartcar.chargeStateCharging') },
    NOT_CHARGING: { variant: 'pending', label: t('smartcar.chargeStateNot') },
  };
  const { variant, label } = map[state];
  return <Badge variant={variant}>{label}</Badge>;
};

/** Single metric card for the data grid */
const MetricCard: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div className="bg-gray-50 rounded-lg px-4 py-3">
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <div className="text-sm font-semibold text-gray-900">{children}</div>
  </div>
);

// ---------------------------------------------------------------------------
// Toast (inline, no external lib)
// ---------------------------------------------------------------------------

const SuccessToast: React.FC<{ message: string; onDismiss: () => void }> = ({ message, onDismiss }) => {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 4000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div className="flex items-center gap-3 bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-3 mb-4">
      <svg className="w-5 h-5 flex-shrink-0 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onDismiss} className="ml-auto text-green-600 hover:text-green-800 transition-colors">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface SmartcarPanelProps {
  vehicleId: string;
}

type PanelState = 'loading' | 'connected' | 'disconnected' | 'error';

export const SmartcarPanel: React.FC<SmartcarPanelProps> = ({ vehicleId }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [panelState, setPanelState] = useState<PanelState>('loading');
  const [data, setData] = useState<SmartcarData | null>(null);
  const [connectMode, setConnectMode] = useState<'live' | 'simulated'>('live');
  const [connecting, setConnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Prevent duplicate initial fetch
  const initialFetchDone = useRef(false);

  const fetchData = useCallback(async (showLoadingState = false) => {
    if (showLoadingState) setPanelState('loading');
    setError(null);
    try {
      const result = await getSmartcarData(vehicleId);
      setData(result);
      setPanelState('connected');
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 409) {
        // Not connected yet
        setPanelState('disconnected');
      } else {
        setPanelState('error');
        setError(t('smartcar.loadError'));
      }
    }
  }, [vehicleId, t]);

  // On mount: check for OAuth callback param first, then load data
  useEffect(() => {
    if (initialFetchDone.current) return;
    initialFetchDone.current = true;

    const smartcarParam = searchParams.get('smartcar');
    if (smartcarParam === 'connected') {
      // Clean up the URL parameter
      const next = new URLSearchParams(searchParams);
      next.delete('smartcar');
      setSearchParams(next, { replace: true });
      // Show success toast then fetch fresh data
      setToast(t('smartcar.connectSuccess'));
      fetchData(true);
    } else {
      fetchData(true);
    }
  }, [fetchData, searchParams, setSearchParams, t]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { url } = await getSmartcarConnectUrl(vehicleId, connectMode);
      window.location.href = url;
    } catch {
      setError(t('smartcar.connectError'));
      setConnecting(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const result = await refreshSmartcarData(vehicleId);
      setData(result);
      setPanelState('connected');
    } catch {
      setError(t('smartcar.refreshError'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t('smartcar.disconnectConfirm'))) return;
    setDisconnecting(true);
    setError(null);
    try {
      await disconnectSmartcar(vehicleId);
      setData(null);
      setPanelState('disconnected');
    } catch {
      setError(t('smartcar.disconnectError'));
    } finally {
      setDisconnecting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const renderSkeleton = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-6 w-20" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} className="h-16" />
        ))}
      </div>
    </div>
  );

  const renderDisconnected = () => (
    <div className="flex flex-col items-center justify-center py-8 text-center gap-4">
      {/* Smartcar logo mark */}
      <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center">
        <svg className="w-6 h-6 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      </div>
      <div>
        <p className="text-sm font-medium text-gray-900 mb-1">{t('smartcar.notConnected')}</p>
        <p className="text-xs text-gray-500 max-w-xs">
          {t('smartcar.connectDescription')}
        </p>
      </div>

      {/* Live / Simulated toggle */}
      <div className="flex items-center bg-gray-100 rounded-lg p-0.5 gap-0.5">
        <button
          onClick={() => setConnectMode('live')}
          className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
            connectMode === 'live'
              ? 'bg-white shadow text-gray-900'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('smartcar.modeLive')}
        </button>
        <button
          onClick={() => setConnectMode('simulated')}
          className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
            connectMode === 'simulated'
              ? 'bg-white shadow text-gray-900'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('smartcar.modeSimulated')}
        </button>
      </div>

      <Button
        variant="primary"
        onClick={handleConnect}
        isLoading={connecting}
        disabled={connecting}
        className="bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500"
      >
        {connecting ? t('smartcar.connecting') : t('smartcar.connectButton')}
      </Button>
    </div>
  );

  const renderConnected = (d: SmartcarData) => {
    const syncedMins = d.fetched_at ? minutesAgo(d.fetched_at) : null;

    return (
      <div className="space-y-4">
        {/* Header row */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900 flex items-center gap-1.5">
              <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Smartcar
            </span>
            <Badge variant="success">
              {/* Green dot */}
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 mr-1" />
              {t('smartcar.connected')}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {/* Refresh icon button */}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('smartcar.refresh')}
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg
                className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
            {/* Disconnect link */}
            <button
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="text-xs text-red-500 hover:text-red-700 hover:underline disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {disconnecting ? t('common.loading') : t('smartcar.disconnect')}
            </button>
          </div>
        </div>

        {/* Data grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">

          {/* Odometer */}
          <MetricCard label={t('smartcar.odometer')}>
            {d.odometer_km != null
              ? `${fmtNumber(Math.round(d.odometer_km))} km`
              : <span className="text-gray-400">—</span>
            }
          </MetricCard>

          {/* Location */}
          <MetricCard label={t('smartcar.location')}>
            {d.latitude != null && d.longitude != null ? (
              <span className="leading-snug">
                {d.location_type === 'LAST_PARKED' && (
                  <span className="block text-xs text-amber-600 font-normal mb-0.5">
                    {t('smartcar.lastParked')}
                  </span>
                )}
                <span className="font-mono text-xs">
                  {d.latitude.toFixed(5)}, {d.longitude.toFixed(5)}
                </span>
              </span>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </MetricCard>

          {/* Fuel */}
          <MetricCard label={t('smartcar.fuel')}>
            {d.fuel_percent != null ? (
              <>
                <span className={
                  d.fuel_percent < 10 ? 'text-red-600' :
                  d.fuel_percent < 25 ? 'text-amber-600' :
                  'text-green-700'
                }>
                  {Math.round(d.fuel_percent)}%
                </span>
                <PercentBar
                  pct={d.fuel_percent}
                  color={
                    d.fuel_percent < 10 ? 'bg-red-500' :
                    d.fuel_percent < 25 ? 'bg-amber-400' :
                    'bg-green-500'
                  }
                />
              </>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </MetricCard>

          {/* Battery % */}
          <MetricCard label={t('smartcar.battery')}>
            {d.battery_percent != null ? (
              <>
                <span className={
                  d.battery_percent < 15 ? 'text-red-600' :
                  d.battery_percent < 30 ? 'text-amber-600' :
                  'text-green-700'
                }>
                  {Math.round(d.battery_percent)}%
                </span>
                <PercentBar
                  pct={d.battery_percent}
                  color={
                    d.battery_percent < 15 ? 'bg-red-500' :
                    d.battery_percent < 30 ? 'bg-amber-400' :
                    'bg-green-500'
                  }
                />
              </>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </MetricCard>

          {/* Battery range */}
          <MetricCard label={t('smartcar.range')}>
            {d.battery_range_km != null
              ? `${fmtNumber(Math.round(d.battery_range_km))} km`
              : <span className="text-gray-400">—</span>
            }
          </MetricCard>

          {/* Charge state */}
          <MetricCard label={t('smartcar.chargeState')}>
            <ChargeStateBadge state={d.charge_state} />
          </MetricCard>

        </div>

        {/* Footer: last sync */}
        {syncedMins !== null && (
          <p className="text-xs text-gray-400 text-right">
            {t('smartcar.lastSynced', { count: syncedMins })}
          </p>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Full panel
  // ---------------------------------------------------------------------------

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Section heading */}
      <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        Smartcar
      </h2>

      {/* Success toast (OAuth callback) */}
      {toast && <SuccessToast message={toast} onDismiss={() => setToast(null)} />}

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 text-sm">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          {error}
        </div>
      )}

      {/* Content states */}
      {panelState === 'loading' && renderSkeleton()}
      {panelState === 'disconnected' && renderDisconnected()}
      {panelState === 'connected' && data && renderConnected(data)}
      {panelState === 'error' && !error && (
        <div className="py-6 text-center">
          <p className="text-sm text-gray-500">{t('smartcar.loadError')}</p>
          <button
            onClick={() => fetchData(true)}
            className="mt-2 text-sm text-indigo-600 hover:underline"
          >
            {t('common.error')}
          </button>
        </div>
      )}
    </div>
  );
};
